/** Maquetación de texto en canvas: ajuste de línea, autoescalado y dibujo. */

export const FONT_STACK =
  '"Segoe UI Variable", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif'

export interface TextLayout {
  lines: string[]
  fontSize: number
  lineHeight: number
  width: number
  height: number
}

export function fontString(size: number, bold = false, italic = false): string {
  return `${italic ? 'italic ' : ''}${bold ? '600 ' : '400 '}${size}px ${FONT_STACK}`
}

/** Parte el texto en líneas que caben en maxWidth, respetando saltos manuales. */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontSize: number,
  bold = false,
  italic = false,
): string[] {
  ctx.font = fontString(fontSize, bold, italic)
  const lines: string[] = []
  for (const paragraph of text.split('\n')) {
    if (paragraph === '') {
      lines.push('')
      continue
    }
    const words = paragraph.split(/(\s+)/)
    let current = ''
    for (const word of words) {
      const candidate = current + word
      if (ctx.measureText(candidate).width <= maxWidth || current === '') {
        current = candidate
      } else {
        lines.push(current.trimEnd())
        current = word.trimStart()
      }
    }
    // Palabras sueltas más largas que la caja: partirlas por caracteres.
    while (ctx.measureText(current).width > maxWidth && current.length > 1) {
      let cut = current.length - 1
      while (cut > 1 && ctx.measureText(current.slice(0, cut)).width > maxWidth) cut--
      lines.push(current.slice(0, cut))
      current = current.slice(cut)
    }
    lines.push(current)
  }
  return lines
}

/**
 * Calcula el tamaño de fuente más grande (≤ maxFontSize) con el que el texto
 * cabe en la caja. Es el comportamiento de las notas adhesivas de Whiteboard.
 */
export function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  boxWidth: number,
  boxHeight: number,
  maxFontSize: number,
  minFontSize = 8,
): TextLayout {
  if (!text) {
    return { lines: [], fontSize: maxFontSize, lineHeight: maxFontSize * 1.25, width: 0, height: 0 }
  }
  let size = maxFontSize
  let lines = wrapText(ctx, text, boxWidth, size)
  while (size > minFontSize && lines.length * size * 1.25 > boxHeight) {
    size = Math.max(minFontSize, size - Math.max(1, Math.round(size * 0.08)))
    lines = wrapText(ctx, text, boxWidth, size)
  }
  const lineHeight = size * 1.25
  let width = 0
  ctx.font = fontString(size)
  for (const line of lines) width = Math.max(width, ctx.measureText(line).width)
  return { lines, fontSize: size, lineHeight, width, height: lines.length * lineHeight }
}

/** Dibuja líneas ya calculadas dentro de una caja. */
export function drawLines(
  ctx: CanvasRenderingContext2D,
  layout: TextLayout,
  box: { x: number; y: number; w: number; h: number },
  align: 'left' | 'center' | 'right',
  verticalAlign: 'top' | 'middle',
  color: string,
  bold = false,
  italic = false,
): void {
  ctx.font = fontString(layout.fontSize, bold, italic)
  ctx.fillStyle = color
  ctx.textBaseline = 'middle'
  ctx.textAlign = align
  const totalHeight = layout.lines.length * layout.lineHeight
  const startY =
    verticalAlign === 'middle'
      ? box.y + box.h / 2 - totalHeight / 2 + layout.lineHeight / 2
      : box.y + layout.lineHeight / 2
  const x = align === 'left' ? box.x : align === 'right' ? box.x + box.w : box.x + box.w / 2
  for (let i = 0; i < layout.lines.length; i++) {
    ctx.fillText(layout.lines[i], x, startY + i * layout.lineHeight)
  }
}

/** Altura necesaria para un texto de ancho fijo (usado por la herramienta Texto). */
export function measureTextHeight(
  ctx: CanvasRenderingContext2D,
  text: string,
  width: number,
  fontSize: number,
  bold = false,
  italic = false,
): number {
  const lines = wrapText(ctx, text || ' ', width, fontSize, bold, italic)
  return Math.max(1, lines.length) * fontSize * 1.25
}
